import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
  Alert,
  Button,
  Stack,
  TextField,
  Container,
  Breadcrumbs,
  Fade,
  Divider,
  Card,
  CardContent,
  Avatar,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip
} from "@mui/material";

// アイコン
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import InboxIcon from "@mui/icons-material/Inbox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import InfoIcon from '@mui/icons-material/Info';
import KeyIcon from '@mui/icons-material/Key';

import Header from "../../../components/Header";
import { useNavigate } from "react-router-dom";

const SettingsSharedJoin = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [serverError, setServerError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const res = await axios.get("/api/shared/owners", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setOwners(res.data);
      } catch (err) {
        setError("共有オーナーの取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    fetchOwners();
  }, []);

  const handleJoin = async (requestId) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.post(`/api/shared/owners/${requestId}/rejoin`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.reload();
    } catch (err) {
      const message = err?.response?.data || "共有への再参加に失敗しました。";
      setServerError(message);
    }
  };

  const handleExit = async (ownerId) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.post("/api/shared/exit", { ownerId }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      window.location.reload();
    } catch (err) {
      alert("共有から抜ける処理に失敗しました。");
    }
  };

  const handleRequest = async () => {
    const trimmedCode = code.trim().toUpperCase();

    if (!trimmedCode) {
      alert("共有コードを入力してください。");
      return;
    }
    if (!/^[A-Z0-9]{6}$/.test(trimmedCode)) {
      alert("共有コードは英数字6桁で入力してください。");
      return;
    }
    try {
      const token = sessionStorage.getItem("token");
      await axios.post("/api/shared/request", { code: trimmedCode }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRequestSuccess(true);
      setServerError(null);
      setCode("");
      setTimeout(() => setRequestSuccess(false), 3000);
    } catch (err) {
      const message = err?.response?.data || "共有申請に失敗しました。";
      setServerError(message);
    }
  };

  return (
    <>
      <Header />
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4, 
          // PWAボトムナビゲーション対応：安全マージンを確保
          mb: { xs: 2, sm: 6 }, 
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          px: { xs: 2, sm: 3 },
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
        }}
      >
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/setting-Home')}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            設定
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/settings/shared')}
          >
            <PeopleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            共有設定
          </Typography>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <InboxIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            共有に参加
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                共有に参加する
              </Typography>
              <Typography color="text.secondary">
                共有コードを入力して他のユーザーのデータにアクセスするか、既存の共有に参加します
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Stack spacing={3}>
              {/* 共有コード入力セクション */}
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    px: 3, 
                    py: 2, 
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <KeyIcon />
                  <Typography variant="subtitle1" fontWeight="medium">
                    新しい共有に参加
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <TextField
                    fullWidth
                    label="共有コードを入力"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="例: ABC123"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="申請を送信">
                            <IconButton 
                              edge="end" 
                              color="primary" 
                              onClick={handleRequest}
                              disabled={!code || code.length !== 6}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    * 共有コードは6桁の英数字です
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleRequest}
                    startIcon={<LockIcon />}
                    disabled={!code || code.length !== 6}
                  >
                    コードで共有申請
                  </Button>

                  {requestSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      申請を送信しました！オーナーの承認をお待ちください。
                    </Alert>
                  )}
                  
                  {serverError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {serverError}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* 利用可能な共有セクション */}
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    px: 3, 
                    py: 2, 
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    <Typography variant="subtitle1" fontWeight="medium">
                      参加可能な共有
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${owners.length}件`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Box>

                <CardContent sx={{ p: 0 }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : error ? (
                    <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                  ) : owners.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <InfoIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography color="text.secondary">
                        現在参加可能な共有はありません
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {owners.map((owner) => (
                        <ListItem
                          key={owner.userId}
                          divider
                          sx={{ 
                            p: 0, 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 2,
                              width: { xs: '100%', sm: 'auto' },
                              flex: 1
                            }}
                          >
                            <Avatar 
                              sx={{ 
                                bgcolor: owner.status === "joined" ? 'success.light' : 'grey.200',
                                color: owner.status === "joined" ? 'success.dark' : 'text.secondary',
                                mr: 2 
                              }}
                            >
                              {owner.displayName?.charAt(0) || owner.username?.charAt(0) || 'U'}
                            </Avatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography fontWeight="medium">
                                    {owner.displayName || owner.username}
                                  </Typography>
                                  {owner.status === "joined" && (
                                    <Chip 
                                      label="参加中" 
                                      size="small" 
                                      color="success" 
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              }
                              secondary={`状態: ${owner.status === "joined" ? "参加中" : "未参加"}`}
                            />
                          </Box>
                          <Box 
                            sx={{ 
                              p: { xs: 2, sm: 1 }, 
                              pt: { xs: 0, sm: 1 },
                              borderTop: { xs: '1px solid', sm: 'none' },
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: { xs: 'stretch', sm: 'flex-end' },
                              width: { xs: '100%', sm: 'auto' }
                            }}
                          >
                            {owner.status === "joined" ? (
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<ExitToAppIcon />}
                                onClick={() => handleExit(owner.userId)}
                                fullWidth={!!(window.innerWidth < 600)}
                              >
                                共有から抜ける
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<LoginIcon />}
                                onClick={() => handleJoin(owner.userId)}
                                fullWidth={!!(window.innerWidth < 600)}
                              >
                                共有に参加する
                              </Button>
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Stack>

            {/* 戻るボタン */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/settings/shared")}
                size="large"
                sx={{ minWidth: 200 }}
              >
                共有設定に戻る
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>
    </>
  );
};

export default SettingsSharedJoin;